import { Box, Button, Divider, IconButton, Typography } from '@mui/material';
import _ from 'lodash';
import { FC, useContext } from 'react';
import api from 'src/common/api/api';
import constants, {
    ANALYSIS_TYPE,
    MAP_PLATFORM_TO_SEARCH_TYPE,
    socialPlatformsMap,
} from 'src/common/constants/constants';
import { GlobalContext } from 'src/common/contexts/globalContext';
import { i18n } from 'src/common/i18n/i18n';
import { IconSearch, IconXWindow } from 'src/common/images';
import { capitalizeFirstLetter } from 'src/common/utils/formatters';
import { isSuccess } from 'src/common/utils/communication';
import { obj } from 'src/common/models/common-types';
import { NOTIFICATION, useNotifications } from '@cellebrite/saas-ui-common';
import { PENDO_EVENTS, pendoTrack } from '../../../../utils/pendo';
import { useQueryClient } from 'react-query';
import { RunAsANewSearchIcon } from '@cellebrite/saas-ui-icons';

interface MatchesActionBarProps {
    id: any;
    possibleMatches: any;
    selectedByPlatform?: any[];
    onClickExit: () => void;
    fetchPossibleMatches: () => void;
    setSelectedByPlatform: (value: any) => void;
    addItemWithExpiry: (id: string) => void;
    selectedIds: string[];
    isButtonDisabled: boolean;
}

const MatchesActionBar: FC<MatchesActionBarProps> = ({
    selectedByPlatform,
    onClickExit,
    id,
    fetchPossibleMatches,
    possibleMatches,
    setSelectedByPlatform,
    addItemWithExpiry,
    selectedIds,
    isButtonDisabled,
}) => {
    const queryClient = useQueryClient();
    const { casesData, workspacesData, collectionData, adminData } = useContext(GlobalContext);
    const [showMessage] = useNotifications();

    const formatDisplayName = (platform: string, account: obj<any>) => {
        let displayName = null;

        if (['thatsthem', 'pipl'].includes(platform)) {
            displayName = account.name
                ? `${account.name} (${account.id.slice(0, 6)})`
                : account.userName
                  ? `${account.userName} (${account.id.slice(0, 6)})`
                  : account.id.slice(0, 6);
        } else if (platform === 'lexisnexis') {
            displayName = account.name ? `${account.name} (${account.id})` : account.id;
        }

        return displayName;
    };

    const isNewSearchDisabled = () => adminData.isQuotaLimitReached || selectedByPlatform['thatsthem']?.length > 0;

    return !!selectedIds.length ? (
        <Box
            sx={{
                width: 677,
                height: 74,
                p: 2,
                position: 'fixed',
                bottom: '56px',
                zIndex: 1,
                left: '50%',
                borderRadius: '8px',
                transform: 'translate(-50%, 0)',
                boxShadow: '0px 6px 40px -4px #919EABB8',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                border: '1px solid',
            }}
            className="background-color-l border-color-bl"
        >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box
                    sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '8px',
                        lineHeight: '24px',
                        fontSize: '18px',
                        fontWeight: '700',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                    className="color-l background-color-b"
                >
                    {selectedIds.length}
                </Box>
                <Typography sx={{ ml: 1 }} variant="s16w500" className="color-bd">
                    {i18n.translate('Selected')}
                </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Button
                    sx={{ whiteSpace: 'nowrap', border: '0px', fontSize: '16px', mr: 1 }}
                    startIcon={<RunAsANewSearchIcon />}
                    color="secondary"
                    disabled={isNewSearchDisabled()}
                    onClick={async () => {
                        const promises = [];
                        const tempAccounts = [];
                        _.forOwn(selectedByPlatform, (accounts, platform) => {
                            accounts.forEach((account) => {
                                tempAccounts.push(account);
                                let displayName = formatDisplayName(platform, account);
                                promises.push(
                                    collectionData.createCollection.mutate({
                                        pendoEventName: PENDO_EVENTS.SEARCH_POSSIBLE_MATCHES_MULTI_SELECT,
                                        newSearch: {
                                            hideMessage: true,
                                            searchTerm:
                                                platform === 'pipl' ? account.id : account.userName || account.id,
                                            searchType: socialPlatformsMap.get(capitalizeFirstLetter(platform)),
                                            analysisType: account.type ? ANALYSIS_TYPE.SOCIAL : ANALYSIS_TYPE.FULL,
                                            ...(displayName && { displayName }),
                                        },
                                    }),
                                );
                            });
                        });
                    }}
                >
                    {i18n.translate('Create a New Search')}
                </Button>
                <Button
                    sx={{ whiteSpace: 'nowrap', border: '0px', fontSize: '16px', mr: 1 }}
                    startIcon={<IconSearch />}
                    disabled={adminData.isQuotaLimitReached || isButtonDisabled}
                    onClick={async () => {
                        const promises = [];
                        const tempAccounts = [];
                        _.forOwn(selectedByPlatform, (accounts, platform) => {
                            accounts.forEach((account) => {
                                const searchType = MAP_PLATFORM_TO_SEARCH_TYPE[platform] || platform;

                                // payload for thatsthem should be treated as pipl
                                const isPIPLPlatform = ['thatsthem', 'pipl'].includes(platform);

                                const searchTerm = isPIPLPlatform ? account.id : account.userName || account.id;
                                const searchDisplayName = isPIPLPlatform
                                    ? formatDisplayName(platform, account)
                                    : account.userName || account.id;
                                tempAccounts.push(account);
                                addItemWithExpiry(account.id);
                                pendoTrack(PENDO_EVENTS.ADD_TO_REPORT_POSSIBLE_MATCHES_MULTI_SELECT, {
                                    searchTerm,
                                    searchType,
                                    searchDisplayName,
                                    streamId: id,
                                    wsId: workspacesData.activeWorkspace.id,
                                    caseId: casesData.activeCaseId,
                                });
                                promises.push(
                                    api.collections.addToReport(
                                        workspacesData.activeWorkspace.id,
                                        casesData.activeCaseId,
                                        id,
                                        {
                                            searchType,
                                            searchTerm,
                                            searchDisplayName,
                                        },
                                    ),
                                );
                            });
                        });

                        const results = await Promise.all(promises);
                        const accountsIdsSuccess = [];
                        results.forEach((result, index) => {
                            if (isSuccess(result)) {
                                showMessage(
                                    i18n.translate('ADD_TO_REPORT_NAME_SUCCESS', {
                                        name: tempAccounts[index].name,
                                    }),
                                    NOTIFICATION.TYPE.SUCCESS,
                                );
                                accountsIdsSuccess.push(tempAccounts[index].id);
                            } else {
                                showMessage(
                                    i18n.translate('ADD_TO_REPORT_NAME_ERROR', {
                                        name: tempAccounts[index].name,
                                    }),
                                    NOTIFICATION.TYPE.ERROR,
                                );
                            }
                            possibleMatches.updateAddedAccounts(accountsIdsSuccess);
                            setSelectedByPlatform({});
                        });
                        if (accountsIdsSuccess.length) {
                            queryClient.invalidateQueries('quotaValid');
                            setTimeout(() => fetchPossibleMatches(), constants.POSSIBLE_MATCHES_POLLING_DELAY);
                        }
                    }}
                >
                    {i18n.translate('Add to this Search')}
                </Button>
                <Divider sx={{ mr: 1, height: 24, mt: 1 }} orientation="vertical" flexItem />
                <IconButton onClick={onClickExit}>
                    <IconXWindow />
                </IconButton>
            </Box>
        </Box>
    ) : null;
};

export default MatchesActionBar;
